import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@material-ui/core";

import RefreshIcon from "@material-ui/icons/Refresh";
import SearchIcon from "@material-ui/icons/Search";
import { useTheme } from "@material-ui/core/styles";
import Alert from "@material-ui/lab/Alert";
import AgentsAppBar, { AGENTS_APP_BAR_OFFSET_PX } from "../components/Agents/AgentsAppBar";
import AgentTaskDetailPane from "../components/Agents/AgentTaskDetailPane";
import { getAgent, getAgentRunDetail, listAgentRunsPage } from "../features/agents/agentApi";
import {
  formatRunWhen,
  formatDurationMs,
  effectiveRunStatus,
  runActionCount,
  runAppIcons,
  runCountsTowardPlanQuota,
  runKindLabel,
  runStatusChipProps,
  runTableDescription,
} from "../features/agents/agentRunFormat";
import { RUN_STATUSES } from "../features/agents/agentTaskTypes";

const LIVE_POLL_MS = 2500;

const isRunInProgress = (run) => {
  if (!run) return false;
  const s = String(effectiveRunStatus(run) || run.status || "").toLowerCase();
  return s === RUN_STATUSES.RUNNING || s === "in_progress" || s === "in-progress";
};

const AgentTasksPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const history = useHistory();
  const { agentId } = useParams();
  const [loading, setLoading] = useState(true);
  const [agentName, setAgentName] = useState("");
  const [actions, setActions] = useState([]);
  const [runs, setRuns] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [detailRun, setDetailRun] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [liveUpdating, setLiveUpdating] = useState(false);
  const [livePulseAt, setLivePulseAt] = useState(null);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [appFilter, setAppFilter] = useState("all");
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [liveTotalFiltered, setLiveTotalFiltered] = useState(null);
  const [runsLoading, setRunsLoading] = useState(false);

  const loadAgent = useCallback(async () => {
    if (!agentId) return;
    setLoading(true);
    try {
      const agent = await getAgent(agentId);
      if (agent) {
        setAgentName(agent.name || agent.agent_id || "Agent");
        setActions(agent.actions || []);
      } else {
        setAgentName("Agent");
        setActions([]);
      }
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    loadAgent();
  }, [loadAgent]);

  const hasActiveFilters = Boolean(
    search.trim() ||
      dateFrom ||
      dateTo ||
      statusFilter !== "all" ||
      appFilter !== "all",
  );

  useEffect(() => {
    setPage(0);
  }, [agentId, search, dateFrom, dateTo, statusFilter, appFilter]);

  useEffect(() => {
    let cancelled = false;
    if (!agentId) return undefined;
    (async () => {
      setRunsLoading(true);
      try {
        const res = await listAgentRunsPage(agentId, {
          limit: rowsPerPage,
          skip: page * rowsPerPage,
          status: statusFilter,
          dateFrom,
          dateTo,
          q: search.trim(),
          app: appFilter,
        });
        if (cancelled) return;
        setRuns(res.runs || []);
        setTotalCount(Number.isFinite(res.total) ? res.total : 0);
        setLiveTotalFiltered(res.liveTotal != null && Number.isFinite(res.liveTotal) ? res.liveTotal : null);
      } catch {
        if (!cancelled) {
          setRuns([]);
          setTotalCount(0);
          setLiveTotalFiltered(null);
        }
      } finally {
        if (!cancelled) setRunsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [agentId, page, rowsPerPage, statusFilter, dateFrom, dateTo, search, appFilter]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(totalCount / rowsPerPage) - 1);
    setPage((p) => (p > maxPage ? maxPage : p));
  }, [totalCount, rowsPerPage]);

  const appOptions = useMemo(() => {
    const seen = new Set();
    (actions || []).forEach((a) => {
      const k = (a.app || a.app_label || "").toString().trim();
      if (k) seen.add(k);
    });
    return [...seen].sort((a, b) => a.localeCompare(b));
  }, [actions]);

  const liveOnPage = useMemo(() => runs.filter((r) => runCountsTowardPlanQuota(r)).length, [runs]);

  const onRefresh = async () => {
    if (!agentId) return;
    setRefreshing(true);
    try {
      const agent = await getAgent(agentId);
      if (agent) {
        setAgentName(agent.name || agent.agent_id || "Agent");
        setActions(agent.actions || []);
      }
      const res = await listAgentRunsPage(agentId, {
        limit: rowsPerPage,
        skip: page * rowsPerPage,
        status: statusFilter,
        dateFrom,
        dateTo,
        q: search.trim(),
        app: appFilter,
      });
      setRuns(res.runs || []);
      setTotalCount(Number.isFinite(res.total) ? res.total : 0);
      setLiveTotalFiltered(res.liveTotal != null && Number.isFinite(res.liveTotal) ? res.liveTotal : null);
    } finally {
      setRefreshing(false);
    }
  };

  const openRow = async (run) => {
    const id = run?.run_id || run?.task_id;
    if (id == null || String(id).trim() === "") return;
    setSelectedRunId(String(id));
    const running = isRunInProgress(run);
    // Running runs: always fetch latest full doc (list rows are often step-light / stale).
    if (!running && Array.isArray(run.steps) && run.steps.length > 0) {
      setDetailRun(run);
      setDetailLoading(false);
      return;
    }
    setDetailLoading(true);
    if (!running) setDetailRun(null);
    else setDetailRun(run); // show what we have while loading
    try {
      const full = await getAgentRunDetail(agentId, String(id));
      setDetailRun(full || run);
    } catch {
      setDetailRun(run);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedRunId(null);
    setDetailRun(null);
    setLiveUpdating(false);
    setLivePulseAt(null);
  };

  const openedRunFromUrl = useRef(false);
  useEffect(() => {
    if (!agentId || openedRunFromUrl.current) return undefined;
    const params = new URLSearchParams(window.location.search);
    const runId = String(params.get("run") || params.get("task") || "").trim();
    if (!runId) return undefined;
    openedRunFromUrl.current = true;
    setSelectedRunId(runId);
    setDetailLoading(true);
    let cancelled = false;
    getAgentRunDetail(agentId, runId)
      .then((full) => {
        if (!cancelled) setDetailRun(full);
      })
      .catch(() => {
        if (!cancelled) setDetailRun(null);
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [agentId]);

  // Live refresh: while the open run is still in progress, poll for new steps.
  useEffect(() => {
    if (!agentId || !selectedRunId) {
      setLiveUpdating(false);
      return undefined;
    }
    if (!isRunInProgress(detailRun)) {
      setLiveUpdating(false);
      return undefined;
    }

    let cancelled = false;
    setLiveUpdating(true);

    const tick = async () => {
      if (cancelled) return;
      try {
        const full = await getAgentRunDetail(agentId, String(selectedRunId));
        if (cancelled || !full) return;
        setDetailRun((prev) => {
          const prevSteps = Array.isArray(prev?.steps) ? prev.steps.length : 0;
          const nextSteps = Array.isArray(full.steps) ? full.steps.length : 0;
          const statusChanged = String(prev?.status || "") !== String(full.status || "");
          if (nextSteps !== prevSteps || statusChanged || !prev) {
            // Defer so we don't nest setStates inside the updater
            setTimeout(() => {
              if (!cancelled) setLivePulseAt(Date.now());
            }, 0);
          }
          return full;
        });
        // Keep the table row in sync (status / step count).
        setRuns((prev) =>
          (prev || []).map((r) => {
            const rid = String(r?.run_id || r?.task_id || "");
            if (rid !== String(selectedRunId)) return r;
            return {
              ...r,
              ...full,
              steps: full.steps || r.steps,
              status: full.status ?? r.status,
            };
          }),
        );
        if (!isRunInProgress(full)) {
          setLiveUpdating(false);
        }
      } catch {
        /* keep last known detail; try again next interval */
      }
    };

    // Immediate refresh, then interval
    tick();
    const timer = setInterval(tick, LIVE_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [agentId, selectedRunId, detailRun?.status]);

  if (loading) {
    return (
      <>
        <AgentsAppBar title="Run history" backTooltip="Back to agent" onBack={() => history.push(`/agents/${agentId}`)} />
        <Box display="flex" justifyContent="center" mt={10} style={{ paddingTop: AGENTS_APP_BAR_OFFSET_PX }}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  return (
    <>
      <AgentsAppBar
        title="Run history"
        backTooltip="Back to agent"
        onBack={() => history.push(`/agents/${agentId}`)}
      />
      <div
      style={{
        padding: isMobile
          ? `${AGENTS_APP_BAR_OFFSET_PX}px 12px 16px 12px`
          : `${AGENTS_APP_BAR_OFFSET_PX}px 24px 24px 24px`,
        minHeight: "100vh",
        width: "100%",
        boxSizing: "border-box",
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Box maxWidth={1100} mx="auto">
        <Box display="flex" alignItems="baseline" flexWrap="wrap" style={{ gap: 12 }} mb={1}>
          <Typography variant="body1" color="textSecondary">
            {agentName}
          </Typography>
        </Box>
        <Typography variant="body2" color="textSecondary" paragraph>
          Each row is one run (trigger tick, webhook, manual test, or preview). Only <strong>Live</strong> runs count
          toward plan quota — preview and editor test runs do not.
        </Typography>

        <Alert severity="info" variant="outlined" style={{ marginBottom: 16 }}>
          <Typography variant="body2">
            Live runs on this page: <strong>{liveOnPage}</strong>
            {liveTotalFiltered != null ? (
              <>
                {" "}
                — <strong>{liveTotalFiltered}</strong> live run{liveTotalFiltered === 1 ? "" : "s"} match current
                filters (all pages).
              </>
            ) : null}{" "}
            Quota counts only <strong>Live</strong> runs (preview / test excluded).
          </Typography>
        </Alert>

        <Paper style={{ padding: 16, marginBottom: 16 }}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Search runs"
                placeholder="run id, status, summary, errors, payload…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="From date"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="To date"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel id="agent-tasks-status-filter">Status</InputLabel>
                <Select
                  labelId="agent-tasks-status-filter"
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All statuses</MenuItem>
                  <MenuItem value="success">Success</MenuItem>
                  <MenuItem value="partial_success">Partial success</MenuItem>
                  <MenuItem value="skipped">Skipped</MenuItem>
                  <MenuItem value="halted">Halted</MenuItem>
                  <MenuItem value="error">Error</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <FormControl variant="outlined" size="small" fullWidth disabled={appOptions.length === 0}>
                <InputLabel id="agent-tasks-app-filter">App</InputLabel>
                <Select
                  labelId="agent-tasks-app-filter"
                  label="App"
                  value={appFilter}
                  onChange={(e) => setAppFilter(e.target.value)}
                >
                  <MenuItem value="all">All apps</MenuItem>
                  {appOptions.map((app) => (
                    <MenuItem key={app} value={app}>
                      {app}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                startIcon={refreshing ? <CircularProgress size={18} /> : <RefreshIcon />}
                disabled={refreshing}
                onClick={onRefresh}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <TableContainer component={Paper} style={{ marginBottom: 24 }}>
          {runsLoading ? <LinearProgress /> : null}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Description</TableCell>
                <TableCell align="right">Actions</TableCell>
                <TableCell>Apps</TableCell>
                <TableCell>When</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!runsLoading && totalCount === 0 && !hasActiveFilters ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="textSecondary">
                      No tasks yet. Start the agent for live runs, or use Preview to test (preview runs are tagged and
                      excluded from quota).
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : !runsLoading && totalCount === 0 && hasActiveFilters ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="textSecondary">
                      No runs match your filters. Try clearing search or widening the date range.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                runs.map((run) => {
                  const icons = runAppIcons(run, actions);
                  const rowKey = run.run_id || run.task_id || JSON.stringify(run);
                  return (
                    <TableRow
                      key={rowKey}
                      hover
                      selected={selectedRunId === String(run.run_id || run.task_id || "")}
                      onClick={() => openRow(run)}
                      style={{ cursor: "pointer" }}
                    >
                      <TableCell>
                        <Typography variant="body2">{runTableDescription(run)}</Typography>
                        {run.latency_ms != null ? (
                          <Typography variant="caption" color="textSecondary" display="block">
                            {formatDurationMs(run.latency_ms)}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell align="right">{runActionCount(run)}</TableCell>
                      <TableCell>
                        <Box display="flex" style={{ gap: 4 }}>
                          {icons.length === 0 ? (
                            <Typography variant="caption" color="textSecondary">
                              —
                            </Typography>
                          ) : (
                            icons.slice(0, 6).map((ic, i) => (
                              <Avatar
                                key={`${ic.app}-${i}`}
                                src={ic.icon_url || undefined}
                                style={{ width: 28, height: 28 }}
                              >
                                {(ic.app || "?").slice(0, 1).toUpperCase()}
                              </Avatar>
                            ))
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {formatRunWhen(run)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 4 }}>
                          <Chip size="small" {...runStatusChipProps(effectiveRunStatus(run))} />
                          {isRunInProgress(run) ? (
                            <Chip
                              size="small"
                              label="Live"
                              style={{
                                height: 22,
                                backgroundColor: "#e8f5e9",
                                color: "#1b5e20",
                                fontWeight: 700,
                              }}
                            />
                          ) : null}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined" label={runKindLabel(run)} />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          {totalCount > 0 ? (
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onChangePage={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onChangeRowsPerPage={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage="Rows per page"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
              }
            />
          ) : null}
        </TableContainer>
      </Box>

      <Drawer
        anchor="right"
        open={Boolean(selectedRunId)}
        onClose={closeDetail}
        PaperProps={{
          style: {
            width: isMobile ? "100%" : 640,
            maxWidth: "100vw",
            overflowX: "hidden",
          },
        }}
      >
        <AgentTaskDetailPane
          run={detailRun}
          loading={detailLoading}
          onClose={closeDetail}
          agentActions={actions}
          agentId={agentId}
          liveUpdating={liveUpdating}
          livePulseAt={livePulseAt}
          onRecapGenerated={(recap) => setDetailRun((prev) => (prev ? { ...prev, recap } : null))}
        />
      </Drawer>
    </div>
    </>
  );
};

export default AgentTasksPage;
